// Bullet patterns as data.
//
// A pattern is a looping timeline of emission steps. Nine emitters and
// a handful of modifiers multiply out to far more distinct-feeling
// attacks than either could alone, and because the whole thing is a
// table, adding an attack is adding a row rather than writing code.
// That difference is what makes 28 patterns affordable instead of
// abandoned at pattern nine.
//
// Angles are **turns** throughout (0 = right, 0.25 = down). See rng.ts.
//
// ── The one design rule everything here obeys ─────────────────────────
//
// The player is a mouse cursor: no inertia, no acceleration curve, no
// top speed worth speaking of. Bullet *speed* is therefore nearly free
// to dodge — you simply are not there any more — and the real
// difficulty axis is **space denial**. So the shmup instinct is
// inverted throughout: fewer, bigger, slower bullets that occupy area
// beat clouds of fast ones. A wall of slow bullets with one authored
// gap is a puzzle; the same bullets at double speed is a non-event.
//
// The playfield is small (a 360–720px window), so 40–80 bullets on
// screen is the practical ceiling before it reads as mush. Touhou can
// hold hundreds against a 1–2px hitbox; we cannot, and pretending
// otherwise just produces noise. `tests/daemon-patterns.test.ts`
// enforces a per-tier ceiling so this stays true as the table grows.

import { angleT, clamp, cosT, dirT, sinT, type Rng } from "./rng";
import {
  ARENA_H,
  ARENA_W,
  NO_SPAWN_R,
  type Bullet,
  type BulletKind,
} from "./types";

/** Base shapes. Each punishes a different habit; see the design spec. */
export type EmitterKind =
  | "aimed" // fires at the cursor — punishes standing still
  | "lead" // fires where the cursor is going — punishes constant velocity
  | "spread" // arc of n — punishes narrow committed positioning
  | "ring" // full circle — punishes being near the source
  | "spiral" // rotating emitter — punishes purely reactive dodging
  | "wall" // line with exactly one gap — punishes not looking ahead
  | "laser" // telegraphed beam — punishes reaction-only play
  | "mine" // delayed burst — punishes greedy positioning
  | "scatter"; // sparse randomness — use lightly, never as the whole card

/** Modifiers multiply the emitters. Keep to two per step: two
 *  overlapping behaviours read as legible, three read as noise. */
export type ModifierKind =
  | "homing" // weak course correction toward the player
  | "delayed" // hangs in place, then launches
  | "mirrored" // a second copy reflected through the arena centre
  | "accel" // slow tell, fast finish
  | "decel" // fast tell, slow drift — good for filling space
  | "bounce" // reflects off the arena edge
  | "dense"; // halves spacing, doubles count

export type EmitStep = {
  /** Tick within the pattern loop at which this step fires. */
  at: number;
  emitter: EmitterKind;
  /** Bullets produced. `dense` doubles it. */
  count: number;
  /** Logical units per tick. Slow and dense is harder for a mouse than
   *  fast and sparse — the cursor has no inertia, so speed is trivially
   *  out-run and space is not. Tune accordingly. */
  speed: number;
  /** Arc width in turns. `spread` uses it as the fan angle; `aimed` and
   *  `lead` use it as the shotgun jitter across `count`. */
  spread?: number;
  /** Angle offset in turns for ring/spiral/laser, a fixed base angle for
   *  `spread`, and the edge selector for `wall` (see the wall case). */
  offset?: number;
  mods?: ModifierKind[];
  kind?: BulletKind;
  damage?: number;
  radius?: number;
  /** Ticks the bullet lives. Defaults to a full arena crossing. */
  ttl?: number;
  /** `spiral` only: rotation in turns per tick. Negative counter-rotates,
   *  which is the whole trick behind the two-armed patterns. */
  rate?: number;
  /** Ticks a `delayed` bullet — or a `mine`, which implies it — hangs
   *  before launching. This is the pattern's own telegraph. */
  hold?: number;
};

export type Pattern = {
  id: string;
  name: string;
  /** Loop length in ticks. Steps repeat every `loop` ticks. */
  loop: number;
  /** Difficulty tier, 1 (runlevel 5) to 5 (boss). Phases draw from
   *  their own tier so escalation is structural, not hand-wired.
   *
   *  Tier is escalation of **kind**, not of number: a tier-5 pattern is
   *  not a tier-1 pattern with the count doubled, it is two or three
   *  ideas the player has already been taught separately, now
   *  overlapping. */
  tier: 1 | 2 | 3 | 4 | 5;
  steps: EmitStep[];
};

// ── The resolver ──────────────────────────────────────────────────────

export type EmitContext = {
  /** Where the bullets come from. */
  ox: number;
  oy: number;
  /** Where the player is, for aimed/lead/homing. */
  px: number;
  py: number;
  /** Player velocity in units/tick, for `lead`. */
  pvx: number;
  pvy: number;
  tick: number;
  rng: Rng;
  /** Allocates replay-stable ids. */
  nextId: () => number;
};

const DEFAULT_TTL = 600;

/** Default hang time for `delayed` and for mines. 26 ticks ≈ 430ms,
 *  which is inside the window a player can actually act on. */
const DEFAULT_HOLD = 26;

/** Minimum clear corridor through a `wall`, in logical units, measured
 *  centre-to-centre of the bullets either side of the gap. Sized so the
 *  gap survives `dense`: without a floor here, doubling the count would
 *  halve the gap and quietly turn a fair wall into a coin flip. */
const MIN_WALL_GAP = 210;

/** How far, as a fraction of the wall, a gap is kept from the lane the
 *  player is already standing in. Big enough that reaching it is a
 *  journey; small enough that it is always an easy one. */
const GAP_LANE = 0.18;

/** Emitters that fire everything from a single point, and so need their
 *  `delayed` volleys spread out to be visible while they hang. */
const ASSEMBLES = new Set<EmitterKind>(["aimed", "lead", "spread", "ring", "spiral", "scatter"]);

/** How far out a held volley assembles, in logical units. */
const ASSEMBLE_R = 64;

/** How far a mine may be dropped from the emitter. */
const MINE_MIN_R = 150;
const MINE_MAX_R = 420;

/** Mines are kept this far inside the arena edge, so a dropped mine is
 *  always a thing you can see and route around rather than an off-screen
 *  spawn that arrives without introduction. */
const MINE_MARGIN = 70;

/**
 * Invariant 1, in one place: no bullet may be born inside the player's
 * no-spawn bubble. Offending spawn points are pushed out rather than
 * dropped, so the pattern keeps its shape.
 */
function shove(
  px: number,
  py: number,
  x: number,
  y: number,
  turns: number,
): { x: number; y: number } {
  const dx = x - px;
  const dy = y - py;
  const d2 = dx * dx + dy * dy;
  if (d2 >= NO_SPAWN_R * NO_SPAWN_R) return { x, y };
  const d = Math.sqrt(d2);
  if (d < 1e-6) {
    // Born exactly on the cursor. There is no outward direction to
    // follow, so use the bullet's own heading — it leaves along the
    // line it was always going to travel.
    const dir = dirT(turns);
    return { x: px + dir.x * NO_SPAWN_R, y: py + dir.y * NO_SPAWN_R };
  }
  const k = (NO_SPAWN_R - d) / d;
  return { x: x + dx * k, y: y + dy * k };
}

function make(
  ctx: EmitContext,
  x: number,
  y: number,
  turns: number,
  speed: number,
  step: EmitStep,
  mods: Set<ModifierKind>,
  delay: number,
): Bullet {
  const d = dirT(turns);
  return {
    id: ctx.nextId(),
    x,
    y,
    vx: d.x * speed,
    vy: d.y * speed,
    r: step.radius ?? 11,
    kind: step.kind ?? "plain",
    damage: step.damage ?? 8,
    born: ctx.tick,
    ttl: step.ttl ?? DEFAULT_TTL,
    homing: mods.has("homing") ? 0.0022 : 0,
    accel: mods.has("accel") ? 1.014 : mods.has("decel") ? 0.986 : 1,
    bounces: mods.has("bounce") ? 2 : 0,
    delay,
    grazed: false,
  };
}

/**
 * Expand one emission step into bullets.
 *
 * Two invariants hold for every emitter, and both are load-bearing for
 * fairness rather than niceties:
 *
 *   1. **No bullet spawns inside NO_SPAWN_R of the player.** With a
 *      cursor hitbox, no dash and no i-frames, a bullet born on top of
 *      you is unfair by construction. Offending bullets are pushed out
 *      along their own heading rather than dropped, so the pattern
 *      keeps its shape. This applies to `mirrored` copies too — the
 *      reflection is a spawn like any other.
 *   2. **`wall` always has exactly one gap, and the gap is authored.**
 *      The genre's named failure is bullets aligning into something
 *      unavoidable; we never hope for a gap, we place one. `mirrored`
 *      is therefore refused on walls: reflecting a wall would fill its
 *      own gap with the mirror image, which is precisely the failure
 *      the invariant exists to prevent.
 */
export function emit(ctx: EmitContext, step: EmitStep): Bullet[] {
  const mods = new Set(step.mods ?? []);
  const count = mods.has("dense") ? step.count * 2 : step.count;
  const out: Bullet[] = [];
  const { ox, oy } = ctx;
  const radius = step.radius ?? 11;
  const held = mods.has("delayed") ? (step.hold ?? DEFAULT_HOLD) : 0;

  const push = (
    x: number,
    y: number,
    turns: number,
    speed = step.speed,
    delay = held,
  ): void => {
    // A held volley from a point source would otherwise park every
    // bullet on the same pixel and read as one bullet until it
    // launched — the telegraph would be invisible, and a dozen bullets
    // of the density budget would be spent on nothing the player can
    // see. Pushing each one out along its own heading makes the volley
    // assemble in the air as the shape it is about to become.
    // `wall`, `laser` and `mine` place themselves and are excluded.
    let sx = x;
    let sy = y;
    if (delay > 0 && ASSEMBLES.has(step.emitter)) {
      const dir = dirT(turns);
      sx += dir.x * ASSEMBLE_R;
      sy += dir.y * ASSEMBLE_R;
    }
    const p = shove(ctx.px, ctx.py, sx, sy, turns);
    out.push(make(ctx, p.x, p.y, turns, speed, step, mods, delay));
  };

  switch (step.emitter) {
    case "aimed": {
      const base = angleT(ox, oy, ctx.px, ctx.py);
      const arc = step.spread ?? (count - 1) * 0.006;
      for (let i = 0; i < count; i++) {
        const t = count === 1 ? 0.5 : i / (count - 1);
        push(ox, oy, base + (t - 0.5) * arc);
      }
      break;
    }

    case "lead": {
      // Aim at where the cursor will be in the time the bullet takes to
      // arrive. Punishes moving in a straight line, which is exactly
      // the habit a new player forms in their first ten seconds.
      const flight = Math.max(1, Math.hypot(ctx.px - ox, ctx.py - oy) / step.speed);
      const tx = ctx.px + ctx.pvx * flight;
      const ty = ctx.py + ctx.pvy * flight;
      const base = angleT(ox, oy, tx, ty);
      const arc = step.spread ?? (count - 1) * 0.008;
      for (let i = 0; i < count; i++) {
        const t = count === 1 ? 0.5 : i / (count - 1);
        push(ox, oy, base + (t - 0.5) * arc);
      }
      break;
    }

    case "spread": {
      // Aimed by default; give `offset` to nail the fan to a fixed
      // heading instead, which is how you author a curtain that does
      // not care where the player happens to be standing.
      const base = step.offset ?? angleT(ox, oy, ctx.px, ctx.py);
      const arc = step.spread ?? 0.16;
      for (let i = 0; i < count; i++) {
        const t = count === 1 ? 0.5 : i / (count - 1);
        push(ox, oy, base + (t - 0.5) * arc);
      }
      break;
    }

    case "ring": {
      const off = step.offset ?? 0;
      for (let i = 0; i < count; i++) push(ox, oy, off + i / count);
      break;
    }

    case "spiral": {
      // The offset advances with the tick, so successive loops lay down
      // a rotating arm. Readable, and it rewards committing to a lane.
      // A negative `rate` counter-rotates — two steps with opposite
      // rates give the crossing arms of `Spinlock Contention`.
      const off = (step.offset ?? 0) + ctx.tick * (step.rate ?? 0.0042);
      for (let i = 0; i < count; i++) push(ox, oy, off + i / count);
      break;
    }

    case "wall": {
      // Invariant 2 — one authored gap, placed away from the player so
      // it is reachable but not free.
      //
      // `offset` picks the edge the wall comes from, in quadrants:
      //   < 0.25 top (falls)   < 0.5 bottom (rises)
      //   < 0.75 right (←)     else  left (→)
      const off = step.offset ?? 0;
      const edge = off < 0.25 ? "top" : off < 0.5 ? "bottom" : off < 0.75 ? "right" : "left";
      const alongX = edge === "top" || edge === "bottom";
      const heading = edge === "top" ? 0.25 : edge === "bottom" ? 0.75 : edge === "right" ? 0.5 : 0;

      // Half-width of the gap, in wall fractions. The `1.15 / count`
      // term keeps at least two slots clear at any count; the second
      // term guarantees a real corridor in logical units regardless of
      // how finely the wall is subdivided.
      const half = Math.max(1.15 / count, (MIN_WALL_GAP / 2 + radius) / ARENA_W);

      // Where the gap sits along the wall, as a fraction. Two
      // constraints, and both are fairness rather than taste:
      //
      //   * It stays far enough from either end that at least one slot
      //     survives on each side. A gap that runs off the end of the
      //     wall is not a gap, it is a shorter wall — and nothing on
      //     screen tells the player which of the two they are looking at.
      //   * It stays out of the lane the player already occupies. A gap
      //     that opens exactly where you stand is not a decision.
      const lo = half + 1 / count;
      const hi = 1 - half - 1 / count;
      const playerT = alongX ? ctx.px / ARENA_W : ctx.py / ARENA_H;
      const loEnd = Math.min(hi, playerT - GAP_LANE);
      const hiStart = Math.max(lo, playerT + GAP_LANE);
      const loLen = Math.max(0, loEnd - lo);
      const hiLen = Math.max(0, hi - hiStart);
      // One draw either way, so the generator advances identically
      // whichever branch is taken and replays stay aligned.
      const roll = ctx.rng();
      let gap: number;
      if (loLen + hiLen <= 0) {
        // Wall too finely divided to honour the lane rule. The single
        // gap matters more than where it is; keep it and move on.
        gap = clamp(lo + roll * Math.max(0, hi - lo), lo, hi);
      } else {
        const u = roll * (loLen + hiLen);
        gap = u < loLen ? lo + u : hiStart + (u - loLen);
      }

      for (let i = 0; i < count; i++) {
        const t = (i + 0.5) / count;
        if (Math.abs(t - gap) < half) continue; // the gap
        const x = alongX ? t * ARENA_W : edge === "right" ? ARENA_W : 0;
        const y = alongX ? (edge === "top" ? 0 : ARENA_H) : t * ARENA_H;
        push(x, y, heading);
      }
      break;
    }

    case "laser": {
      // A rigid lance of overlapping bullets along one heading. It is
      // built *behind* the emitter and rips forward through it, so the
      // beam arrives as a train rather than materialising in the
      // player's lap — and so the no-spawn shove never has to crush the
      // near end of it into a clump.
      //
      // Every authored laser step pairs this with `delayed`, which
      // parks the whole lance in place before it launches: the attack
      // telegraphs itself, out of the pattern data, rather than relying
      // on the sim to draw a warning for it. If the sim later grows a
      // `laser` telegraph, the hold can come down — but it should not
      // go to zero, because the parked lance is also what makes the
      // *angle* readable.
      const base = step.offset ?? angleT(ox, oy, ctx.px, ctx.py);
      const dir = dirT(base);
      const gap = radius * 2.1;
      for (let i = 0; i < count; i++) {
        push(ox - dir.x * i * gap, oy - dir.y * i * gap, base);
      }
      break;
    }

    case "mine": {
      // Drops bullets away from the emitter that hang, then launch at
      // wherever the player was standing when they were dropped. The
      // hang is implied — a mine with no fuse is just a bullet — and
      // staggered slightly so a field of them ripples rather than
      // detonating as one indistinguishable event.
      const fuse = step.hold ?? 60;
      for (let i = 0; i < count; i++) {
        const a = ctx.rng();
        const r = ctx.rng.range(MINE_MIN_R, MINE_MAX_R);
        const mx = clamp(ox + cosT(a) * r, MINE_MARGIN, ARENA_W - MINE_MARGIN);
        const my = clamp(oy + sinT(a) * r, MINE_MARGIN, ARENA_H - MINE_MARGIN);
        const aim = angleT(mx, my, ctx.px, ctx.py);
        push(mx, my, aim, step.speed, fuse + i * 3);
      }
      break;
    }

    case "scatter": {
      for (let i = 0; i < count; i++) push(ox, oy, ctx.rng());
      break;
    }
  }

  // `mirrored` doubles the pattern through the arena's vertical centre
  // line. Halves the authoring cost, doubles the density, and preserves
  // a centre-line safe lane — which keeps it fair rather than merely
  // harder. Refused on walls (see invariant 2).
  if (mods.has("mirrored") && step.emitter !== "wall") {
    const reflected: Bullet[] = [];
    for (const b of out) {
      const mx = ARENA_W - b.x;
      const mvx = -b.vx;
      // Skip an exact self-image. Emitters that are already symmetric
      // about the centre line (a ring fired from x = ARENA_W/2, say)
      // would otherwise stack two bullets in one place: double the
      // density budget spent on a single visible threat.
      const twin = out.some(
        (o) =>
          Math.abs(o.x - mx) < 0.5 &&
          Math.abs(o.y - b.y) < 0.5 &&
          Math.abs(o.vx - mvx) < 0.01 &&
          Math.abs(o.vy - b.vy) < 0.01,
      );
      if (twin) continue;
      const p = shove(ctx.px, ctx.py, mx, b.y, angleT(0, 0, mvx, b.vy));
      reflected.push({ ...b, id: ctx.nextId(), x: p.x, y: p.y, vx: mvx });
    }
    out.push(...reflected);
  }

  return out;
}

// ── The table ─────────────────────────────────────────────────────────
//
// Twenty-eight patterns across five tiers. **Order is load-bearing**:
// `SpellCard.pattern` in phases.ts is an index into this array, so
// entries are appended, never reordered or spliced out.
//
// Tier 1 teaches one idea per pattern. Tier 2 puts two in the same
// loop. Tier 3 starts modifying the shapes rather than adding to them.
// Tier 4 runs two devices against each other. Tier 5 combines things
// the player already knows and trusts them to have learned it.

export const PATTERNS: Pattern[] = [
  // ── Tier 1 — one idea each, and the idea is legible in one loop ────
  {
    id: "hello",
    name: "Hello, Operator",
    tier: 1,
    loop: 150,
    steps: [
      { at: 0, emitter: "aimed", count: 1, speed: 2.4, radius: 16, ttl: 400 },
      { at: 75, emitter: "aimed", count: 1, speed: 2.4, radius: 16, ttl: 400 },
    ],
  },
  {
    id: "sigpipe",
    name: "SIGPIPE Cascade",
    tier: 1,
    loop: 120,
    steps: [
      { at: 0, emitter: "spread", count: 5, speed: 2.8, spread: 0.18 },
      { at: 60, emitter: "aimed", count: 2, speed: 3.4 },
    ],
  },
  {
    id: "roundrobin",
    name: "Round Robin",
    tier: 2,
    // Loop widened from 96 so two slow rings are not still on screen
    // when the next two arrive — at the kindest `nice` that stacked to
    // ~68 concurrent bullets, which is tier-5 density on a tier-2 card.
    loop: 128,
    steps: [
      { at: 0, emitter: "ring", count: 10, speed: 2.2 },
      { at: 64, emitter: "ring", count: 10, speed: 2.2, offset: 0.05 },
    ],
  },
  {
    id: "orphans",
    name: "Orphaned Children",
    tier: 2,
    loop: 180,
    steps: [
      { at: 0, emitter: "spiral", count: 4, speed: 2.0, mods: ["mirrored"] },
      { at: 40, emitter: "spiral", count: 4, speed: 2.0, mods: ["mirrored"] },
      { at: 80, emitter: "spiral", count: 4, speed: 2.0, mods: ["mirrored"] },
      { at: 130, emitter: "lead", count: 3, speed: 3.6, kind: "heavy", damage: 12 },
    ],
  },
  {
    id: "nightly",
    name: "Nightly Rebuild",
    tier: 2,
    // Loop widened from 200 for the same reason as Round Robin: walls
    // this slow take nine seconds to cross, so a 200-tick loop had four
    // of them in the air at once.
    loop: 260,
    steps: [
      { at: 0, emitter: "wall", count: 14, speed: 1.8 },
      { at: 100, emitter: "wall", count: 14, speed: 1.8, offset: 0.6 },
      { at: 160, emitter: "aimed", count: 1, speed: 4.2, kind: "heavy", damage: 14 },
    ],
  },

  // Teaches: the cursor's own velocity is information the enemy has.
  {
    id: "polling",
    name: "Polling Interval",
    tier: 1,
    loop: 168,
    steps: [
      { at: 0, emitter: "lead", count: 3, speed: 2.0, spread: 0.05, radius: 14 },
      { at: 84, emitter: "lead", count: 3, speed: 2.0, spread: 0.05, radius: 14 },
    ],
  },
  // Teaches: distance from the source is safety. Sparse enough that the
  // lesson lands before the punishment does.
  {
    id: "broadcast",
    name: "Broadcast Storm",
    tier: 1,
    loop: 220,
    steps: [
      { at: 0, emitter: "ring", count: 8, speed: 1.6, radius: 15, ttl: 320 },
      { at: 110, emitter: "ring", count: 8, speed: 1.6, radius: 15, offset: 0.0625, ttl: 320 },
    ],
  },
  // Teaches: look at where the gap *is*, not at the bullets. Slow on
  // purpose — the whole attack is a ten-second walk to one door.
  {
    id: "logrotate",
    name: "Log Rotation",
    tier: 1,
    loop: 260,
    steps: [
      { at: 0, emitter: "wall", count: 12, speed: 2.5, radius: 13 },
      { at: 150, emitter: "aimed", count: 2, speed: 3.0 },
    ],
  },
  // Teaches: bullets that stop are still bullets. First taste of space
  // denial — the fan brakes and hangs there, and the arena gets smaller.
  {
    id: "blocking",
    name: "Blocking Read",
    tier: 1,
    loop: 150,
    steps: [
      {
        at: 0,
        emitter: "spread",
        count: 9,
        speed: 3.2,
        spread: 0.3,
        radius: 15,
        ttl: 210,
        mods: ["decel"],
      },
      { at: 70, emitter: "aimed", count: 1, speed: 3.0, kind: "heavy", damage: 12 },
    ],
  },
  // ── Tier 2 — two ideas per loop, still one at a time ───────────────

  // Mines under a shotgun. The fuse announces itself and then keeps its
  // promise, so the route is plannable — but something else is already
  // asking you to move while you plan it.
  {
    id: "reaper",
    name: "The Reaper Runs Hourly",
    tier: 2,
    loop: 200,
    steps: [
      { at: 0, emitter: "mine", count: 6, speed: 2.4, hold: 70, radius: 14 },
      { at: 60, emitter: "spread", count: 5, speed: 2.6, spread: 0.2 },
      { at: 110, emitter: "mine", count: 6, speed: 2.4, hold: 70, radius: 14 },
    ],
  },
  // Slow tell, fast finish. The fan crawls out looking harmless and
  // then leaves at four times the speed it arrived with.
  {
    id: "priority",
    name: "Priority Inversion",
    tier: 2,
    loop: 132,
    steps: [
      { at: 0, emitter: "spread", count: 5, speed: 1.0, spread: 0.22, radius: 14, mods: ["accel"] },
      { at: 66, emitter: "ring", count: 8, speed: 1.9, ttl: 320 },
    ],
  },
  // First lance. It parks itself in the open for half a second before
  // it moves, which is the entire lesson: read, then dodge.
  {
    id: "keepalive",
    name: "Keepalive Probe",
    tier: 2,
    loop: 180,
    steps: [
      { at: 0, emitter: "laser", count: 8, speed: 5.0, radius: 12, mods: ["delayed"], hold: 34 },
      { at: 90, emitter: "laser", count: 8, speed: 5.0, radius: 12, mods: ["delayed"], hold: 34 },
      { at: 140, emitter: "aimed", count: 2, speed: 3.0, spread: 0.04 },
    ],
  },

  // ── Tier 3 — the shapes start getting modified, not just stacked ───

  // `:(){ :|:& };:` — a ring doubled in place, then a faster sparse ring
  // through the gaps it leaves. Density as the whole point.
  {
    id: "forkbomb",
    name: "Fork Bomb",
    tier: 3,
    loop: 220,
    steps: [
      { at: 0, emitter: "ring", count: 9, speed: 1.7, radius: 13, ttl: 300, mods: ["dense"] },
      { at: 110, emitter: "ring", count: 7, speed: 2.6, offset: 0.071 },
      { at: 180, emitter: "aimed", count: 1, speed: 3.8, kind: "heavy", damage: 14 },
    ],
  },
  // Big, slow, and it does not accept your first answer. The homing arc
  // is wide enough to orbit, so these become terrain rather than shots.
  {
    id: "retransmit",
    name: "Retransmit Timeout",
    tier: 3,
    loop: 200,
    steps: [
      {
        at: 0,
        emitter: "aimed",
        count: 3,
        speed: 1.9,
        spread: 0.05,
        radius: 17,
        ttl: 300,
        mods: ["homing"],
      },
      { at: 50, emitter: "ring", count: 8, speed: 2.4 },
      {
        at: 100,
        emitter: "aimed",
        count: 3,
        speed: 1.9,
        spread: 0.05,
        radius: 17,
        ttl: 300,
        mods: ["homing"],
      },
      { at: 160, emitter: "lead", count: 2, speed: 3.4 },
    ],
  },
  // Everything comes back. The edges stop being an escape and start
  // being a return path.
  {
    id: "bouncebuf",
    name: "Bounce Buffer",
    tier: 3,
    loop: 160,
    steps: [
      { at: 0, emitter: "ring", count: 7, speed: 2.4, radius: 13, ttl: 420, mods: ["bounce"] },
      { at: 80, emitter: "spread", count: 5, speed: 2.8, spread: 0.24 },
    ],
  },
  // Two arms turning against each other. Purely reactive dodging fails
  // here: you have to pick a rotation and travel with it.
  {
    id: "spinlock",
    name: "Spinlock Contention",
    tier: 3,
    loop: 128,
    steps: [
      { at: 0, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 0, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 16, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 16, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 32, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 32, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 48, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 48, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 64, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 64, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 80, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 80, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 96, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 96, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
      { at: 112, emitter: "spiral", count: 1, speed: 2.6, rate: 0.0035, radius: 15, ttl: 260 },
      { at: 112, emitter: "spiral", count: 1, speed: 2.6, rate: -0.0035, offset: 0.5, radius: 15, ttl: 260 },
    ],
  },
  // The only card built on `scatter`, and even here it is anchored: the
  // debris brakes to a halt and becomes furniture, and the aimed shot
  // is what actually threatens you while you pick your way through it.
  {
    id: "coredump",
    name: "Core Dumped",
    tier: 3,
    loop: 200,
    steps: [
      { at: 0, emitter: "scatter", count: 12, speed: 3.6, radius: 14, ttl: 190, mods: ["decel"] },
      { at: 60, emitter: "scatter", count: 12, speed: 3.6, radius: 14, ttl: 190, mods: ["decel"] },
      { at: 120, emitter: "scatter", count: 12, speed: 3.6, radius: 14, ttl: 190, mods: ["decel"] },
      { at: 150, emitter: "aimed", count: 2, speed: 3.4, kind: "heavy", damage: 12 },
    ],
  },
  // A sweeping fan of lances across the lower arena. You cannot cross a
  // lance; you move ahead of the sweep or you slip behind it.
  {
    id: "dmesg",
    name: "dmesg Sweep",
    tier: 3,
    loop: 210,
    steps: [
      { at: 0, emitter: "laser", count: 5, speed: 4.4, offset: 0.16, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 18, emitter: "laser", count: 5, speed: 4.4, offset: 0.21, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 36, emitter: "laser", count: 5, speed: 4.4, offset: 0.26, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 54, emitter: "laser", count: 5, speed: 4.4, offset: 0.31, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 115, emitter: "laser", count: 5, speed: 4.4, offset: 0.31, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 133, emitter: "laser", count: 5, speed: 4.4, offset: 0.26, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 151, emitter: "laser", count: 5, speed: 4.4, offset: 0.21, radius: 12, hold: 22, mods: ["delayed"] },
      { at: 169, emitter: "laser", count: 5, speed: 4.4, offset: 0.16, radius: 12, hold: 22, mods: ["delayed"] },
    ],
  },

  // ── Tier 4 — two devices in play at once, arguing ──────────────────

  // The wall gives you one place to stand. The heavies follow you to it.
  {
    id: "oomkiller",
    name: "The OOM Killer",
    tier: 4,
    loop: 240,
    steps: [
      { at: 0, emitter: "wall", count: 13, speed: 2.3, radius: 13 },
      {
        at: 40,
        emitter: "aimed",
        count: 2,
        speed: 1.7,
        spread: 0.04,
        radius: 20,
        ttl: 340,
        kind: "heavy",
        damage: 14,
        mods: ["homing"],
      },
      { at: 120, emitter: "wall", count: 13, speed: 2.3, offset: 0.6, radius: 13 },
      {
        at: 160,
        emitter: "aimed",
        count: 2,
        speed: 1.7,
        spread: 0.04,
        radius: 20,
        ttl: 340,
        kind: "heavy",
        damage: 14,
        mods: ["homing"],
      },
    ],
  },
  // Mirrored arms invalidate one half of the arena at a time while the
  // mines quietly reserve the other half.
  {
    id: "tlbshoot",
    name: "TLB Shootdown",
    tier: 4,
    loop: 180,
    steps: [
      { at: 0, emitter: "spiral", count: 2, speed: 2.4, rate: 0.004, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 20, emitter: "spiral", count: 2, speed: 2.4, rate: 0.004, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 40, emitter: "spiral", count: 2, speed: 2.4, rate: 0.004, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 60, emitter: "spiral", count: 2, speed: 2.4, rate: 0.004, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 80, emitter: "spiral", count: 2, speed: 2.4, rate: 0.004, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 100, emitter: "mine", count: 5, speed: 2.6, hold: 60, radius: 14 },
      { at: 140, emitter: "lead", count: 3, speed: 3.2, spread: 0.05 },
    ],
  },
  // Two walls on perpendicular axes, sixty ticks apart. Each has its
  // own gap; you have a full second to get from one to the other, and
  // the ring is there to make you spend it deliberately.
  {
    id: "deadlock",
    name: "Deadlock Detected",
    tier: 4,
    loop: 280,
    steps: [
      { at: 0, emitter: "wall", count: 12, speed: 2.6, radius: 13 },
      { at: 60, emitter: "wall", count: 12, speed: 2.6, offset: 0.6, radius: 13 },
      { at: 140, emitter: "ring", count: 10, speed: 1.6, radius: 14, ttl: 300 },
      { at: 200, emitter: "wall", count: 12, speed: 2.6, offset: 0.8, radius: 13 },
    ],
  },
  // The braking ring shrinks the room; the accelerating lances punish
  // whatever you were doing while you read it.
  {
    id: "racecond",
    name: "Race Condition",
    tier: 4,
    loop: 200,
    steps: [
      { at: 0, emitter: "ring", count: 12, speed: 3.0, radius: 13, ttl: 230, mods: ["decel"] },
      { at: 60, emitter: "lead", count: 3, speed: 1.2, spread: 0.05, radius: 15, mods: ["accel"] },
      { at: 100, emitter: "ring", count: 12, speed: 3.0, radius: 13, offset: 0.04, ttl: 230, mods: ["decel"] },
      { at: 150, emitter: "lead", count: 3, speed: 1.2, spread: 0.05, radius: 15, mods: ["accel"] },
    ],
  },
  // A charged volley: twelve bullets assemble in the open, hang, and
  // then leave together as a wide curtain.
  {
    id: "irqstorm",
    name: "Interrupt Storm",
    tier: 4,
    loop: 190,
    steps: [
      {
        at: 0,
        emitter: "spread",
        count: 6,
        speed: 2.2,
        spread: 0.5,
        radius: 13,
        hold: 40,
        mods: ["dense", "delayed"],
      },
      {
        at: 60,
        emitter: "spread",
        count: 6,
        speed: 2.2,
        spread: 0.34,
        radius: 13,
        hold: 40,
        mods: ["dense", "delayed"],
      },
      { at: 130, emitter: "aimed", count: 2, speed: 4.0, spread: 0.03 },
    ],
  },
  // A thick, slow wall you have to live inside, swept by lances that
  // cross the corridor. The gap is wide; standing still in it is not
  // enough.
  {
    id: "writebarrier",
    name: "Write Barrier",
    tier: 4,
    loop: 270,
    steps: [
      { at: 0, emitter: "wall", count: 11, speed: 2.3, radius: 12, mods: ["dense"] },
      { at: 70, emitter: "laser", count: 5, speed: 4.6, offset: 0.3, radius: 12, hold: 24, mods: ["delayed"] },
      { at: 90, emitter: "laser", count: 5, speed: 4.6, offset: 0.26, radius: 12, hold: 24, mods: ["delayed"] },
      { at: 110, emitter: "laser", count: 5, speed: 4.6, offset: 0.22, radius: 12, hold: 24, mods: ["delayed"] },
      { at: 190, emitter: "lead", count: 3, speed: 3.0, spread: 0.06 },
    ],
  },

  // ── Tier 5 — combinations, at the density ceiling ──────────────────

  // D state: nothing here can be interrupted. Two braking rings turn
  // the upper arena into furniture, then a wall makes you move through
  // it anyway.
  {
    id: "dstate",
    name: "Uninterruptible Sleep",
    tier: 5,
    loop: 260,
    steps: [
      { at: 0, emitter: "ring", count: 14, speed: 3.4, radius: 14, ttl: 260, mods: ["decel"] },
      { at: 70, emitter: "ring", count: 14, speed: 4.2, radius: 14, offset: 0.036, ttl: 260, mods: ["decel"] },
      { at: 140, emitter: "wall", count: 12, speed: 2.0, radius: 13 },
      { at: 200, emitter: "aimed", count: 2, speed: 3.6, spread: 0.04, kind: "heavy", damage: 14 },
    ],
  },
  // Every verb at once, in sequence: mines reserve, leads accelerate,
  // the wall commits you, the lance closes the loop.
  {
    id: "sig9",
    name: "Signal 9 Cannot Be Caught",
    tier: 5,
    loop: 240,
    steps: [
      { at: 0, emitter: "mine", count: 6, speed: 3.0, hold: 80, radius: 14 },
      { at: 30, emitter: "lead", count: 4, speed: 1.1, spread: 0.06, radius: 14, mods: ["accel"] },
      { at: 60, emitter: "ring", count: 10, speed: 2.0, radius: 13, ttl: 300 },
      { at: 90, emitter: "wall", count: 14, speed: 2.4, radius: 13 },
      { at: 150, emitter: "lead", count: 4, speed: 1.1, spread: 0.06, radius: 14, mods: ["accel"] },
      { at: 190, emitter: "laser", count: 6, speed: 5.0, radius: 12, hold: 30, mods: ["delayed"] },
    ],
  },
  // Not syncing. The bouncing ring refuses to leave, and everything
  // else has to be dodged inside what it leaves behind.
  {
    id: "panic",
    name: "Kernel Panic — Not Syncing",
    tier: 5,
    loop: 280,
    steps: [
      { at: 0, emitter: "ring", count: 8, speed: 2.6, radius: 12, ttl: 420, mods: ["dense", "bounce"] },
      { at: 80, emitter: "spread", count: 7, speed: 2.2, spread: 0.4, radius: 14, ttl: 220, mods: ["decel"] },
      { at: 140, emitter: "wall", count: 12, speed: 2.2, offset: 0.3, radius: 13 },
      { at: 210, emitter: "aimed", count: 3, speed: 3.6, spread: 0.05, kind: "heavy", damage: 14 },
    ],
  },
  // The last card. Counter-rotating mirrored arms, a lance sweep across
  // them, mines under both, and a wall to end on.
  {
    id: "initdead",
    name: "Init Is Not Responding",
    tier: 5,
    loop: 280,
    steps: [
      { at: 0, emitter: "spiral", count: 2, speed: 2.5, rate: -0.0038, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 24, emitter: "spiral", count: 2, speed: 2.5, rate: -0.0038, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 48, emitter: "spiral", count: 2, speed: 2.5, rate: -0.0038, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 72, emitter: "spiral", count: 2, speed: 2.5, rate: -0.0038, radius: 13, ttl: 280, mods: ["mirrored"] },
      { at: 100, emitter: "laser", count: 5, speed: 4.8, offset: 0.34, radius: 12, hold: 24, mods: ["delayed"] },
      { at: 118, emitter: "laser", count: 5, speed: 4.8, offset: 0.3, radius: 12, hold: 24, mods: ["delayed"] },
      { at: 136, emitter: "laser", count: 5, speed: 4.8, offset: 0.26, radius: 12, hold: 24, mods: ["delayed"] },
      { at: 170, emitter: "mine", count: 6, speed: 2.8, hold: 70, radius: 14 },
      { at: 220, emitter: "wall", count: 13, speed: 2.2, radius: 13 },
    ],
  },
];

export const patternById = (id: string): Pattern | undefined =>
  PATTERNS.find((p) => p.id === id);

export const patternsForTier = (tier: number): Pattern[] =>
  PATTERNS.filter((p) => p.tier === tier);
